import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { StorageService } from '../services/storage.service';
import { ExamEventTrackerService } from '../services/exam-event-tracker.service';
import { TestSeriesApiEndpoints } from '../../shared/enum/api-endpoints.enum';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const toastr = inject(ToastrService);
  const router = inject(Router);
  const storageService = inject(StorageService);
  const eventTracker = inject(ExamEventTrackerService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      console.error('HTTP error:', error);

      // No-ops when there's no active attempt (e.g. login/dashboard calls),
      // so this stays scoped to failures during an active exam session.
      // Answer-save and question-load failures are reported by the question
      // paper as ANSWER_SAVE_FAILED / QUESTION_LOAD_FAILED instead, per the
      // event contract.
      if (!isAnswerSaveRequest(req.url) && !isQuestionLoadRequest(req.url)) {
        eventTracker.track('API_REQUEST_FAILED', { endpoint: toEndpointPath(req.url) });
      }

      if (error.status === 401) {
        storageService.clear();
        toastr.error('Your session has expired. Please log in again.');
        void router.navigate(['/login'], { replaceUrl: true });
        return throwError(() => error);
      }

      // Start-exam answers 409 while this or another exam is live on another
      // device or platform; its message says which ("... on the mobile app").
      if (error.status === 409 && isStartExamRequest(req.url)) {
        // Kept up longer than the default: it explains why the start was blocked.
        toastr.warning(extractErrorMessage(error), 'Exam in progress elsewhere', { timeOut: 8000 });
        return throwError(() => error);
      }

      showErrorToast(toastr, extractErrorMessage(error));
      return throwError(() => error);
    }),
  );
};

/**
 * Several requests often fail together (e.g. the dashboard's parallel calls when the
 * backend is down), so only one error toast is shown until it's dismissed.
 */
let errorToastVisible = false;

function showErrorToast(toastr: ToastrService, message: string): void {
  if (errorToastVisible) {
    return;
  }

  const toast = toastr.error(message);
  if (!toast) {
    return;
  }

  errorToastVisible = true;
  toast.onHidden.subscribe(() => (errorToastVisible = false));
}

/** API_REQUEST_FAILED.endpoint is the request path only — no host, and no query string (it can carry tokens). */
function toEndpointPath(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url.split('?')[0];
  }
}

function isAnswerSaveRequest(url: string): boolean {
  return url.includes(TestSeriesApiEndpoints.SUBMIT_EXAM);
}

function isStartExamRequest(url: string): boolean {
  return new RegExp(`/examinations/[^/]+/${TestSeriesApiEndpoints.START_EXAM_SUFFIX}$`).test(
    url.split('?')[0],
  );
}

function isQuestionLoadRequest(url: string): boolean {
  return /\/examinations\/[^/]+\/questions$/.test(url.split('?')[0]);
}

/** Gateway/proxy errors come back as nginx HTML pages, so they get a fixed message instead. */
const GATEWAY_ERROR_MESSAGES: Record<number, string> = {
  502: 'Bad Gateway. Please try again later.',
  503: 'Service Unavailable. Please try again later.',
  504: 'Gateway Timeout. Please try again later.',
};

function isHtml(text: string): boolean {
  return /^\s*<(!doctype|html|head|body)\b/i.test(text);
}

function extractErrorMessage(error: HttpErrorResponse): string {
  const gatewayMessage = GATEWAY_ERROR_MESSAGES[error.status];
  if (gatewayMessage) {
    return gatewayMessage;
  }

  const errorPayload = error.error;

  if (typeof errorPayload === 'string' && errorPayload.trim()) {
    return isHtml(errorPayload) ? 'Internal Server Error. Please try again later.' : errorPayload ||"Bad Gateway. Please try again later.";
  }

  if (errorPayload && typeof errorPayload === 'object') {
    const message = (errorPayload as { message?: unknown }).message;

    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  // error.message is Angular's "Http failure response for <url>: ..." text, which isn't user-facing.
  if (error.status === 0) {
    return 'Unable to reach the server. Please check your connection.';
  }

  return 'Something went wrong. Please try again.';
}
